// The Cancellation tab, served from the rollup payload.
//
// Like Revenue, this reads the same rows Utilization does, so every figure is
// buildTotalsFromRollup or buildDrillFromRollup output -- the reason split, the
// completed/canceled counts and the lead-time sum all ride on MetricTotals
// rather than being reduced a second time here.
//
// Lead time needed a schema change to get here: canceled_appointment_lead_time_days
// existed only on scheduling_fact, so a summed week had no way to answer the
// "Avg cancellation lead time" card. It is summed on the rollups and divided by
// the cancelled count at read time, because an average of averages over unequal
// weeks is not the average.

import { useMemo } from "react";
import type { FilterState, Granularity, ScopeFilters } from "@/types/filters";
import { makeTotalsReader, matchesScope } from "@/lib/rollupMetrics";
import { buildDrillFromRollup } from "@/lib/rollupDrill";
import { splitRange } from "@/lib/rollupSplit";
import { EMPTY_RANGE, getPeriodFamily, getPriorYearRange, type DateRange } from "@/lib/periods";
import { CANCEL_REASONS, type MetricTotals } from "@/lib/metrics";
import type { RollupPayload } from "@/hooks/useRollupData";
import type {
  CancellationKPIs,
  CancellationDrillRow,
  ReasonMixItem,
  CancelReasonKey,
} from "@/hooks/useCancellationData";

function forWindow(
  payload: RollupPayload,
  range: DateRange,
  g: Granularity,
  f: "trailing" | "calendar"
) {
  const split = splitRange(range, g, f);
  const weeks = new Set(split.wholeWeeks);
  const days = new Set(split.edgeDays);
  return {
    siteWeeks: payload.siteWeeks.filter((r) => weeks.has(String(r.week_start))),
    dvmWeeks: payload.dvmWeeks.filter((r) => weeks.has(String(r.week_start))),
    edgeRows: payload.edgeRows.filter((r) => days.has(r.schedule_date)),
  };
}

// "Booked" is completed + cancelled. The dataset has no true booked count, and
// the raw hook uses the same proxy -- changing it here would make the two paths
// disagree about a number neither of them can actually source.
const booked = (t: MetricTotals): number => t.totalCanceled + t.totalCompleted;
const cancelRate = (t: MetricTotals): number => (booked(t) > 0 ? t.totalCanceled / booked(t) : 0);
const noShowRate = (t: MetricTotals): number =>
  booked(t) > 0 ? t.canceledByReason.no_show / booked(t) : 0;

export interface RollupCancellation {
  kpis: CancellationKPIs;
  reasonMix: ReasonMixItem[];
  drillRows: CancellationDrillRow[];
}

export function useRollupCancellation(
  payload: RollupPayload | null,
  filters: FilterState,
  granularity: Granularity
): RollupCancellation | null {
  const { customStart, customEnd, period, scope, excludeZeroApptDays, drillLevel } = filters;
  return useMemo(() => {
    if (!payload) return null;

    const family = getPeriodFamily(period);
    const current: DateRange = { startDate: customStart, endDate: customEnd };
    const priorYear = getPriorYearRange(current, period) ?? EMPTY_RANGE;
    const cur = forWindow(payload, current, granularity, family);
    const py = forWindow(payload, priorYear, granularity, family);

    // makeTotalsReader owns the wide-vs-narrow rule; see rollupMetrics.ts.
    const totals = (part: ReturnType<typeof forWindow>, s: ScopeFilters) =>
      makeTotalsReader({
        part,
        payloadScope: scope,
        wideDvmRows: payload.wideDvmRows,
        excludeZeroApptDays,
      })(s);

    const cy = totals(cur, scope);
    const pyT = totals(py, scope);
    // The raw hook decides this on whether any prior-year ROW survived the
    // filters, not on whether it summed to anything. dvmDays is the rollup
    // equivalent: it is zero exactly when no day survived.
    const hasPy = pyT.dvmDays > 0;

    const kpis: CancellationKPIs = {
      totalCanceled: cy.totalCanceled,
      totalBooked: booked(cy),
      cancelRate: cancelRate(cy),
      noShowRate: noShowRate(cy),
      avgCancelLeadDays: cy.totalCanceled > 0 ? cy.totalCancelLeadDays / cy.totalCanceled : null,
      pyTotalCanceled: hasPy ? pyT.totalCanceled : null,
      pyCancelRate: hasPy ? cancelRate(pyT) : null,
      pyNoShowRate: hasPy ? noShowRate(pyT) : null,
      pyAvgCancelLeadDays:
        hasPy && pyT.totalCanceled > 0 ? pyT.totalCancelLeadDays / pyT.totalCanceled : null,
    };

    const reasonMix: ReasonMixItem[] = CANCEL_REASONS.map((key) => ({
      key: key as CancelReasonKey,
      count: cy.canceledByReason[key],
      pct: cy.totalCanceled > 0 ? cy.canceledByReason[key] / cy.totalCanceled : 0,
      pyCount: hasPy ? pyT.canceledByReason[key] : null,
    }));

    // A DVM row is sublabelled with the site where most of their cancellations
    // happened -- the site NAME alone here, unlike Revenue's "id - name".
    const subLabels = new Map<string, string>();
    if (drillLevel === "dvm") {
      const siteNames = new Map<string, string>();
      for (const s of payload.siteWeeks) {
        siteNames.set(String(s.location_id), String(s.location_name ?? ""));
      }
      for (const r of payload.edgeRows) {
        if (!siteNames.has(r.location_id)) siteNames.set(r.location_id, r.location_name);
      }
      const byDvmSite = new Map<string, number>();
      const bump = (dvm: string, site: string, n: number) => {
        const k = dvm + "|" + site;
        byDvmSite.set(k, (byDvmSite.get(k) ?? 0) + n);
      };
      for (const d of cur.dvmWeeks) {
        if (!matchesScope(d, scope)) continue;
        const row = d as unknown as Record<string, unknown>;
        const n = Number(
          excludeZeroApptDays ? row.canceled_appointments_nonzero : row.canceled_appointments
        );
        bump(String(d.resource_id), String(d.location_id), n || 0);
      }
      for (const r of cur.edgeRows) {
        if (!matchesScope(r, scope)) continue;
        if (excludeZeroApptDays && Number(r.completed_appointment_minutes) <= 0) continue;
        bump(r.resource_id, r.location_id, Number(r.canceled_appointments) || 0);
      }
      const best = new Map<string, { site: string; n: number; name: string }>();
      for (const [key, n] of byDvmSite) {
        const sep = key.indexOf("|");
        const dvm = key.slice(0, sep);
        const site = key.slice(sep + 1);
        const held = best.get(dvm);
        // Compared by NAME, matching the raw path: ties are broken by what the
        // user sees, and two ids can map to the same name.
        const name = siteNames.get(site) ?? "";
        if (!held || n > held.n || (n === held.n && name < held.name)) {
          best.set(dvm, { site, n, name });
        }
      }
      for (const [dvm, held] of best) {
        subLabels.set(dvm, siteNames.get(held.site) ?? "");
      }
    }

    const pyById = new Map<string, MetricTotals>();
    for (const e of buildDrillFromRollup({ ...py, scope, drillLevel, excludeZeroApptDays })) {
      pyById.set(e.id, e.totals);
    }

    const drillRows: CancellationDrillRow[] = buildDrillFromRollup({
      ...cur,
      scope,
      drillLevel,
      excludeZeroApptDays,
    })
      .map((e) => {
        let topReason: CancelReasonKey | null = null;
        let topCount = 0;
        for (const key of CANCEL_REASONS) {
          if (e.totals.canceledByReason[key] > topCount) {
            topCount = e.totals.canceledByReason[key];
            topReason = key as CancelReasonKey;
          }
        }
        const p = pyById.get(e.id);
        return {
          id: e.id,
          label: e.label,
          subLabel: drillLevel === "dvm" ? (subLabels.get(e.id) ?? null) : null,
          booked: booked(e.totals),
          canceled: e.totals.totalCanceled,
          cancelRate: cancelRate(e.totals),
          noShows: e.totals.canceledByReason.no_show,
          noShowRate: noShowRate(e.totals),
          topReason,
          pyCancelRate: p && booked(p) > 0 ? cancelRate(p) : null,
        };
      })
      .sort((a, b) => b.cancelRate - a.cancelRate);

    return { kpis, reasonMix, drillRows };
  }, [
    payload,
    granularity,
    customStart,
    customEnd,
    period,
    scope,
    excludeZeroApptDays,
    drillLevel,
  ]);
}
